import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { alpha } from '@mui/material/styles';

import {
  CS_FONT,
  CS_WEIGHT,
  callGroundSurface,
  csTokens,
} from 'components/CallScribe/callScribeTheme';

const END_DIAMETER = 64;

const usePopupCallViewStyles = makeStyles((theme: Theme) => ({
  betweenCalls: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: theme.spacing(2),
    width: '100%',
    maxWidth: 360,
    margin: '0 auto',
    padding: theme.spacing(1, 0),
    boxSizing: 'border-box',
    color: csTokens(theme).onGround,
  },
  betweenCallsHead: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  betweenCallsTitle: {
    margin: 0,
    ...CS_FONT.title,
    fontWeight: CS_WEIGHT.heavy,
    color: csTokens(theme).onGround,
  },
  betweenCallsHint: {
    margin: 0,
    ...CS_FONT.small,
    color: csTokens(theme).onGroundMuted,
    maxWidth: 320,
  },
  betweenCallsAgain: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    minHeight: 52,
    width: '100%',
    padding: theme.spacing(0, 2),
    border: 'none',
    borderRadius: 12,
    background: csTokens(theme).stageCall,
    color: csTokens(theme).stageCallInk,
    fontFamily: 'inherit',
    ...CS_FONT.body,
    fontWeight: CS_WEIGHT.strong,
    fontVariantNumeric: 'tabular-nums',
    cursor: 'pointer',
    transition: 'filter 150ms ease-out',
    '& svg': { fontSize: 20 },
    '&:hover:not(:disabled)': { filter: 'brightness(1.06)' },
    '&:focus-visible': {
      outline: `2px solid ${csTokens(theme).stageFocus}`,
      outlineOffset: 2,
    },
    '&:disabled': { cursor: 'default', opacity: 0.6 },
  },
  betweenCallsDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    ...CS_FONT.caption,
    fontWeight: CS_WEIGHT.strong,
    color: csTokens(theme).onGroundMuted,
    '&::before, &::after': {
      content: '""',
      flex: '1 1 auto',
      height: 1,
      background: csTokens(theme).glassBorder,
    },
  },
  betweenCallsField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  betweenCallsLabel: {
    ...CS_FONT.small,
    fontWeight: CS_WEIGHT.strong,
    color: csTokens(theme).onGroundMuted,
  },
  betweenCallsInput: {
    height: 48,
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(0, 1.75),
    borderRadius: 12,
    border: `1px solid ${csTokens(theme).onGroundFaint}`,
    background: csTokens(theme).glass,
    color: csTokens(theme).onGround,
    caretColor: csTokens(theme).onGround,
    fontFamily: 'inherit',
    ...CS_FONT.body,
    fontVariantNumeric: 'tabular-nums',
    transition: 'border-color 150ms ease-out',
    '&:hover:not(:disabled)': { borderColor: csTokens(theme).onGroundMuted },
    '&:focus-visible': {
      outline: `2px solid ${csTokens(theme).stageFocus}`,
      outlineOffset: 2,
      borderColor: csTokens(theme).onGroundMuted,
    },
    '&:disabled': { opacity: 0.6 },
  },
  betweenCallsOther: {
    minHeight: 48,
    width: '100%',
    padding: theme.spacing(0, 2),
    border: 'none',
    borderRadius: 12,
    background: csTokens(theme).glass,
    boxShadow: `inset 0 0 0 1px ${csTokens(theme).glassBorder}`,
    color: csTokens(theme).onGround,
    fontFamily: 'inherit',
    ...CS_FONT.body,
    fontWeight: CS_WEIGHT.strong,
    cursor: 'pointer',
    transition: 'background-color 150ms ease-out',
    '&:hover:not(:disabled)': { background: csTokens(theme).glassStrong },
    '&:focus-visible': {
      outline: `2px solid ${csTokens(theme).stageFocus}`,
      outlineOffset: 2,
    },
    '&:disabled': { cursor: 'default', opacity: 0.5 },
  },
  root: {
    position: 'relative',
    height: '100vh',
    width: '100%',
    ...callGroundSurface(theme),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    overflow: 'hidden',
    '@media (max-height: 799px)': {
      padding: theme.spacing(1),
    },
  },
  stage: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    width: '100%',
  },
  stageActive: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 10,
    width: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    '@media (max-height: 799px)': {
      gap: 4,
    },
  },
  micRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
    ...theme.typography.caption,
    color: csTokens(theme).onGroundMuted,
    minWidth: 0,
  },
  micSelect: {
    ...theme.typography.caption,
    maxWidth: 160,
    color: csTokens(theme).onGroundMuted,
    '& .MuiSelect-select': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .MuiSelect-icon': {
      color: csTokens(theme).onGroundMuted,
    },
  },
  controlsZone: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
  },
  endButton: {
    width: END_DIAMETER,
    height: END_DIAMETER,
    maxWidth: '100%',
    flex: '0 0 auto',
    borderRadius: 999,
    border: 'none',
    background: csTokens(theme).stageEnd,
    color: csTokens(theme).stageEndInk,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    transition: 'filter 150ms ease-out, transform 120ms ease-out',
    '&:hover': {
      filter: 'brightness(1.06)',
    },
    '&:active': {
      transform: 'scale(0.96)',
    },
    '&:focus-visible': {
      outline: `2px solid ${csTokens(theme).stageFocus}`,
      outlineOffset: 3,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'filter 150ms ease-out',
      '&:active': {
        transform: 'none',
      },
    },
  },
  endButtonSmall: {
    width: 56,
    height: 56,
  },
  keypadOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 10,
    background: csTokens(theme).ground,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(3, 2.5),
    boxSizing: 'border-box',
  },
  dtmfDisplay: {
    ...CS_FONT.display,
    fontWeight: CS_WEIGHT.strong,
    letterSpacing: 2,
    fontVariantNumeric: 'tabular-nums',
    color: csTokens(theme).onGround,
    minHeight: theme.spacing(5),
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  overlayActions: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    width: '100%',
    maxWidth: 320,
    gap: theme.spacing(1),
  },
  overlayEndSlot: {
    gridColumn: 2,
    display: 'flex',
    justifyContent: 'center',
  },
  hideButton: {
    gridColumn: 3,
    justifySelf: 'end',
    minHeight: 44,
    border: 'none',
    background: 'transparent',
    ...CS_FONT.small,
    fontWeight: CS_WEIGHT.strong,
    color: csTokens(theme).onGround,
    cursor: 'pointer',
    padding: theme.spacing(0, 1.5),
    borderRadius: 10,
    transition: 'background-color 150ms ease-out',
    '&:hover': {
      background: csTokens(theme).glass,
    },
    '&:focus-visible': {
      outline: `2px solid ${csTokens(theme).stageFocus}`,
      outlineOffset: 2,
    },
  },
  endedCard: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(3, 2),
    textAlign: 'center',
  },
  endedGlyph: {
    width: 72,
    height: 72,
    borderRadius: 999,
    background: csTokens(theme).glassStrong,
    boxShadow: `0 0 0 3px ${csTokens(theme).ground}, 0 0 0 5px ${csTokens(theme).glassBorder}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: csTokens(theme).onGround,
    marginBottom: theme.spacing(1.5),
  },
  endedTitle: {
    ...CS_FONT.display,
    fontWeight: CS_WEIGHT.heavy,
    color: csTokens(theme).onGround,
  },
  endedHint: {
    ...theme.typography.body2,
    color: csTokens(theme).onGroundMuted,
    maxWidth: 320,
  },
  actionButton: {
    '&.MuiButton-root': {
      textTransform: 'none',
      ...CS_FONT.body,
      fontWeight: CS_WEIGHT.strong,
      marginTop: theme.spacing(1),
      minHeight: 44,
      minWidth: 160,
      padding: theme.spacing(1, 3),
      borderRadius: 8,
      color: csTokens(theme).ground,
      background: csTokens(theme).onGround,
      '&:hover': {
        background: csTokens(theme).onGroundMuted,
      },
      '&.Mui-focusVisible': {
        outline: `2px solid ${csTokens(theme).onGround}`,
        outlineOffset: 3,
      },
      '&.Mui-disabled': {
        color: csTokens(theme).ground,
        background: csTokens(theme).onGroundMuted,
      },
    },
  },
  quietButton: {
    '&.MuiButton-root': {
      textTransform: 'none',
      ...CS_FONT.body,
      fontWeight: CS_WEIGHT.strong,
      minHeight: 44,
      minWidth: 120,
      padding: theme.spacing(1, 3),
      borderRadius: 8,
      color: csTokens(theme).onGround,
      '&:hover': {
        background: csTokens(theme).glass,
      },
      '&.Mui-focusVisible': {
        outline: `2px solid ${csTokens(theme).onGround}`,
        outlineOffset: 2,
      },
    },
  },
  noticePanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    background: alpha(csTokens(theme).stageWarn, 0.14),
    boxShadow: `inset 0 0 0 1px ${alpha(csTokens(theme).stageWarn, 0.45)}`,
    minWidth: 0,
  },
  noticeTitle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    ...CS_FONT.small,
    fontWeight: CS_WEIGHT.strong,
    color: csTokens(theme).onGround,
    '& svg': { fontSize: 18, color: csTokens(theme).stageWarn },
  },
  noticeBody: {
    ...CS_FONT.small,
    color: csTokens(theme).onGroundMuted,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    minWidth: 0,
  },
  errorPanel: {
    borderRadius: 16,
    background: alpha(csTokens(theme).stageEnd, 0.14),
    boxShadow: `inset 0 0 0 1px ${alpha(csTokens(theme).stageEnd, 0.5)}`,
    padding: theme.spacing(1.75, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    maxWidth: 400,
    width: '100%',
    boxSizing: 'border-box',
  },
  errorTitle: {
    ...theme.typography.body2,
    fontWeight: 600,
    color: csTokens(theme).onGround,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  errorBody: {
    ...theme.typography.body2,
    color: csTokens(theme).onGroundMuted,
    wordBreak: 'break-word',
  },
  ringingZone: {
    flex: '1 0 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (max-height: 799px)': {
      '& > ol > li': { paddingTop: 2, paddingBottom: 2 },
    },
  },
}));

export default usePopupCallViewStyles;
